import {
  useDamageExceptionById,
  useUpdateDamageExceptionStatus,
} from '@/api/DamageExceptions';
import { faClose, Icon, LicensePlate } from '@/ui/components';
import {
  Alert,
  Button,
  Col,
  Empty,
  Modal,
  Row,
  Select,
  Spin,
  Tooltip,
} from 'antd';
import React, { FC, useState } from 'react';
import { useTheme } from 'styled-components';
import { VehicleDetail } from './VehicleDetail';
import { Notes } from './Notes';
import { DamageExceptionStatus, Select as SelectOption } from '@/types';
import { Images } from './Images';
import { Actions } from './Actions';
import { DamageDetail } from './DamageDetail';

interface DamageExceptionDetailProps {
  damageExceptionId: number;
  vin: string;
}
export const DamageExceptionDetail: FC<DamageExceptionDetailProps> = ({
  damageExceptionId,
  vin,
}) => {
  const theme = useTheme();
  const [clickId, setClickId] = useState<number>();
  const [error, setError] = useState<string>();
  const { data, isFetching } = useDamageExceptionById(clickId);
  const detail = data?.detail;
  const vehicle = data?.vehicle;
  const statusList: SelectOption[] = [
    { label: 'Open', value: DamageExceptionStatus.Open.toString() },
    { label: 'In Review', value: DamageExceptionStatus.InReview.toString() },
    { label: 'Actioned', value: DamageExceptionStatus.Actioned.toString() },
    { label: 'Closed', value: DamageExceptionStatus.Closed.toString() },
  ];

  const { mutateAsync: updateStatusAsync, isPending: statusLoading } =
    useUpdateDamageExceptionStatus(damageExceptionId);

  const onChangeStatus = async (status: DamageExceptionStatus) => {
    setError(undefined);
    const res = await updateStatusAsync({ damageExceptionId, status });
    if (!res.success) {
      setError(res.message.Message);
      return;
    }
    setError(undefined);
  };

  return (
    <>
      <Tooltip title="Click to view damage & exception detail">
        <span
          className="cursor-pointer"
          onClick={() => setClickId(damageExceptionId)}
        >
          {vin}
        </span>
      </Tooltip>
      <Modal
        open={!!clickId}
        centered
        closable={false}
        footer={null}
        title={
          <div className="d-flex align-items-center justify-content-between p-2">
            <LicensePlate regNumber={vehicle?.regNumber} vin={vehicle?.vin} />
            <span className="fw-semibold fs-5" style={{ color: 'white' }}>
              {vehicle?.manufactureDate
                ? `${vehicle?.manufactureDate.split('-')[0]} `
                : null}
              {vehicle?.model}
            </span>
            <Button type="text" onClick={() => setClickId(undefined)}>
              <Icon icon={faClose} color="white" />
            </Button>
          </div>
        }
        width={{
          xs: '90%',
          sm: '80%',
          md: '76%',
          lg: '70%',
          xl: '65%',
          xxl: '60%',
        }}
        styles={{
          content: {
            padding: 0,
          },
          header: {
            backgroundColor: theme.primaryBg,
          },
        }}
      >
        <Spin spinning={isFetching}>
          {!vehicle ? (
            <Empty />
          ) : (
            <div
              className="px-4 pb-4"
              style={{ height: '80vh', overflowY: 'scroll' }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="fw-bold">
                  {detail?.subCustomerName ?? detail?.customerName}
                </span>
                <div>
                  <Select
                    className="dark-select"
                    style={{ width: 120 }}
                    value={
                      detail
                        ? DamageExceptionStatus[detail.status as any].toString()
                        : undefined
                    }
                    options={statusList}
                    disabled={statusLoading}
                    onChange={(value) =>
                      onChangeStatus(value as unknown as DamageExceptionStatus)
                    }
                  />
                </div>
              </div>
              {error ? (
                <Alert message={error} type="error" className="my-2" />
              ) : null}
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <VehicleDetail vehicle={vehicle} />
                  <Notes
                    damageExceptionId={damageExceptionId}
                    notes={data.notes}
                  />
                </Col>
                <Col xs={24} lg={12}>
                  <DamageDetail
                    detail={data.detail}
                    originalInfo={data.originalInfo}
                  />
                  <Images
                    damageExceptionId={damageExceptionId}
                    images={data.images}
                    attachments={data.attachments}
                  />
                  <Actions
                    damageExceptionId={damageExceptionId}
                    actions={data.actions}
                    jobs={data.jobs}
                  />
                </Col>
              </Row>
            </div>
          )}
        </Spin>
      </Modal>
    </>
  );
};
